import {
  BreadcrumbDivider,
  Button,
  Icon,
} from "@screencloud/screencloud-ui-components";
import { MouseEvent, useEffect, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import Avatar from "src/components/Admin/Avatar";
import { CompareColumn } from "./components/CompareColumn/CompareColumn";
import { useFindCounterpart } from "./hooks/useFindCounterpart";
import { useItemPreview } from "./hooks/useItemPreview";
import {
  CompareErrorState,
  CompareLoadingState,
  CompareNoChangeState,
} from "./components/CompareStates/CompareStates";
import {
  Body,
  BodyFrame,
  CloseButton,
  Container,
  CurrentCrumb,
  Footer,
  Header,
  ParentCrumb,
  PreviewContent,
  PreviewPane,
  Title,
  TitleBreadcrumb,
  TitleSlot,
} from "./styles";
import { CompareChangesModalProps } from "./types";
import { formatPublishedDate } from "./utils/formatDate";

/**
 * Presentational Compare Changes content. Rendered inside the app modal manager
 * (`context.modal.openModal`) which owns the overlay/positioning. It receives a
 * computed diff via props and never fetches, computes the diff, or exposes a
 * refresh control.
 */
export const CompareChangesModal = ({
  status = "loaded",
  currentlyLive,
  changes,
  isPublishing = false,
  onClose,
  onPublish,
  onRetry,
  canPreviewItem,
  renderItemPreview,
  canOpenItem,
  onOpenItem,
}: CompareChangesModalProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const { highlight, findItem } = useFindCounterpart(bodyRef);
  const { previewItem, startPreview, endPreview } = useItemPreview();
  const canPreview = renderItemPreview ? canPreviewItem : undefined;
  const previewPaneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (previewItem) {
      previewPaneRef.current?.focus();
    }
  }, [previewItem]);
  // Row hover is mirrored onto the matching row in the other column.
  const [hoveredId, setHoveredId] = useState<string>();
  // Changes-side rows can only find counterparts that exist in the live list
  // (added rows have none).
  const liveItemIds = new Set(
    (currentlyLive?.items ?? []).map((item) => item.id),
  );
  const totalChanges = changes?.totalChanges ?? 0;
  const changeItems = changes?.items ?? [];
  // Treat a true no-change result and an empty comparison result (no rows to
  // display) as the same user-facing state — never render an empty table.
  const hasPublishableChanges = totalChanges > 0 && changeItems.length > 0;
  const isNoChangeState = status === "loaded" && !hasPublishableChanges;
  const canPublish =
    status === "loaded" && hasPublishableChanges && !isPublishing;
  const previewing = Boolean(
    previewItem &&
      renderItemPreview &&
      status === "loaded" &&
      hasPublishableChanges,
  );

  const title = (
    <FormattedMessage
      id="compare_changes.title"
      defaultMessage="Compare Changes"
    />
  );

  const publishedOn = (value?: string | Date) => {
    if (!value) {
      return undefined;
    }
    const formatted = formatPublishedDate(value);
    if (!formatted) {
      return undefined;
    }
    return (
      <FormattedMessage
        id="compare_changes.published_on"
        defaultMessage="Published on {date}"
        values={{ date: formatted }}
      />
    );
  };

  const changesAside = changes?.lastEditedBy ? (
    <>
      <FormattedMessage
        id="compare_changes.last_edited_by"
        defaultMessage="Last edited by {name}"
        values={{ name: changes.lastEditedBy.name }}
      />
      <Avatar
        className="avatar"
        size={16}
        name={changes.lastEditedBy.name}
        url={changes.lastEditedBy.avatarUrl}
        dataTestid="compare-changes-last-edited-avatar"
      />
    </>
  ) : undefined;

  const renderFooter = () => {
    // Unified error state: the only action is Retry, which the owning page
    // routes to either recompute the diff or re-attempt the publish.
    if (status === "error") {
      return (
        <Button data-testid="compare-changes-retry" onClick={onRetry}>
          <FormattedMessage
            id="compare_changes.error.retry"
            defaultMessage="Retry"
          />
        </Button>
      );
    }
    if (isNoChangeState) {
      return (
        <Button data-testid="compare-changes-close-footer" onClick={onClose}>
          <FormattedMessage id="compare_changes.close" defaultMessage="Close" />
        </Button>
      );
    }
    return (
      <>
        <Button data-testid="compare-changes-not-now" onClick={onClose}>
          <FormattedMessage
            id="compare_changes.not_now"
            defaultMessage="Not Now"
          />
        </Button>
        <Button
          primary
          data-testid="compare-changes-publish"
          disabled={!canPublish}
          onClick={onPublish}
        >
          {isPublishing ? (
            <FormattedMessage
              id="compare_changes.publishing"
              defaultMessage="Publishing…"
            />
          ) : (
            <FormattedMessage
              id="compare_changes.publish"
              defaultMessage="Publish Changes"
            />
          )}
        </Button>
      </>
    );
  };

  const renderBody = () => {
    if (status === "loading") {
      return (
        <CompareLoadingState
          publishedOn={publishedOn(currentlyLive?.publishedAt)}
        />
      );
    }
    if (status === "error") {
      return <CompareErrorState />;
    }
    if (!hasPublishableChanges) {
      return <CompareNoChangeState />;
    }
    return (
      <BodyFrame>
        <Body
          data-testid="compare-changes-body"
          ref={bodyRef}
          $hidden={previewing}
          aria-hidden={previewing || undefined}
        >
          <CompareColumn
            variant="live"
            testId="compare-changes-currently-live"
            onFindItem={(id) => findItem("changes", id)}
            highlightedId={
              highlight?.column === "live" ? highlight.id : undefined
            }
            onHoverItem={setHoveredId}
            linkedHoverId={hoveredId}
            headerTitle={
              <FormattedMessage
                id="compare_changes.currently_live"
                defaultMessage="Currently live"
              />
            }
            headerSubtitle={publishedOn(currentlyLive?.publishedAt)}
            items={currentlyLive?.items ?? []}
            settingChanges={currentlyLive?.settingChanges}
            canPreviewItem={canPreview}
            onPreviewItem={startPreview}
            canOpenItem={canOpenItem}
            onOpenItem={onOpenItem}
          />
          <CompareColumn
            variant="changes"
            testId="compare-changes-changes"
            onFindItem={(id) => findItem("live", id)}
            findableIds={liveItemIds}
            highlightedId={
              highlight?.column === "changes" ? highlight.id : undefined
            }
            linkedHoverId={hoveredId}
            onHoverItem={setHoveredId}
            headerTitle={
              <FormattedMessage
                id="compare_changes.changes_to_publish"
                defaultMessage="Changes to publish"
              />
            }
            headerSubtitle={
              <FormattedMessage
                id="compare_changes.total_changes"
                defaultMessage="{count} total changes"
                values={{ count: totalChanges }}
              />
            }
            headerAside={changesAside}
            items={changes?.items ?? []}
            settingChanges={changes?.settingChanges}
            canPreviewItem={canPreview}
            onPreviewItem={startPreview}
            canOpenItem={canOpenItem}
            onOpenItem={onOpenItem}
          />
        </Body>
        {previewing && previewItem && renderItemPreview && (
          <PreviewPane
            ref={previewPaneRef}
            tabIndex={-1}
            data-testid="compare-changes-preview"
          >
            <PreviewContent>{renderItemPreview(previewItem)}</PreviewContent>
          </PreviewPane>
        )}
      </BodyFrame>
    );
  };

  return (
    <Container data-testid="compare-changes-modal">
      <Header>
        {previewing && previewItem ? (
          // Studio's in-modal drill-down pattern (see the Activity modal).
          <TitleSlot>
            <TitleBreadcrumb>
              <ParentCrumb
                href="#"
                data-testid="compare-changes-preview-back"
                onClick={(event: MouseEvent) => {
                  event.preventDefault();
                  endPreview();
                }}
              >
                {title}
              </ParentCrumb>
              <BreadcrumbDivider />
              <CurrentCrumb
                aria-current="page"
                title={previewItem.title}
                data-testid="compare-changes-preview-title"
              >
                {previewItem.title}
              </CurrentCrumb>
            </TitleBreadcrumb>
          </TitleSlot>
        ) : (
          <Title>{title}</Title>
        )}
        <CloseButton
          icon
          transparent
          aria-label="Close"
          data-testid="compare-changes-close"
          onClick={onClose}
        >
          <Icon name="close" />
        </CloseButton>
      </Header>

      {renderBody()}

      {/* No publishing from a preview; the breadcrumb leads back. */}
      {!previewing && <Footer>{renderFooter()}</Footer>}
    </Container>
  );
};
